import { LayoutDashboard, FileText, Globe, Settings, Menu, Shield, ShieldCheck, BarChart, Webhook, Search, CheckSquare, ChevronDown, ChevronUp, Monitor, Bot, Filter, Zap, PhoneCall, Building2, Files, FilterX, Database, MessageSquare, Rocket, Pin, PinOff, GitBranch, Construction, DollarSign, ClipboardCopy, Library, FlaskConical, History, WalletCards, Archive, Fingerprint, ReceiptText, BookOpen, Bell, ClipboardCheck, LineChart, RadioTower, ShieldAlert, TableProperties, SearchCheck, ScanSearch, Handshake, Headphones, Megaphone, Wrench, Inbox, Lightbulb, MessageSquarePlus } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState, type ComponentType, type MouseEvent } from 'react';
import { useSidebar } from '../contexts/SidebarContext';
import { usePendingReviewsCount } from '@/hooks/useQAData';
import { useAuth } from '@/contexts/AuthContext';
import { useAppView } from '@/contexts/AppViewContext';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { vendorCrmEnabled } from '@/features/vendorCrm/config';

type MenuItem = {
  icon: ComponentType<{ className?: string }>;
  label: string;
  path: string;
  to?: string;
  showTransferAlert?: boolean;
  children?: MenuItem[];
};

type NavGroup = {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  items: MenuItem[];
  /** Maturity sections read quieter than the working areas above them. */
  muted?: boolean;
};

export function Sidebar() {
  const location = useLocation();
  const { isCollapsed, toggleSidebar, closeMobileSidebar } = useSidebar();
  const [pinnedPaths, setPinnedPaths] = useState<string[]>([]);
  const { data: pendingCount } = usePendingReviewsCount();
  const { user, profile } = useAuth();
  const { viewMode, canAccessPath } = useAppView();

  // ---------------------------------------------------------------- items

  const dashboardMenuItem: MenuItem = { icon: LayoutDashboard, label: 'Dashboard', path: '/' };
  const domainWhitelistMenuItem: MenuItem = { icon: Globe, label: 'Domain Whitelist', path: '/domains' };
  const domainDirectoryMenuItem: MenuItem = { icon: Building2, label: 'Domain Directory', path: '/domain-directory' };
  const domainProcessingMenuItem: MenuItem = { icon: Filter, label: 'Domain Processing', path: '/domain-processing' };

  const atbsMenuItem: MenuItem = {
    icon: ShieldAlert,
    label: 'ATBS',
    path: '/atbs',
    children: [
      {
        icon: ClipboardCheck,
        label: 'Review queue',
        path: '/atbs',
        to: '/atbs?dateRange=last7d&reviewStatus=unreviewed&transferOnly=true',
        showTransferAlert: true,
      },
      { icon: RadioTower, label: 'Campaign pixels', path: '/atbs/campaign-pixels' },
      { icon: GitBranch, label: 'Prompt audit', path: '/atbs/prompts' },
      { icon: BookOpen, label: 'Documentation', path: '/atbs/documentation' },
    ],
  };

  const historicalMenuItem: MenuItem = {
    icon: Archive,
    label: 'Historical ReAudit',
    path: '/historical-reaudit',
    children: [
      { icon: BarChart, label: 'Overview', path: '/historical-reaudit' },
      { icon: Search, label: 'Call Explorer', path: '/historical-reaudit/calls' },
      { icon: Building2, label: 'Campaign Analytics', path: '/historical-reaudit/campaigns' },
      { icon: Fingerprint, label: 'Themes & Phrases', path: '/historical-reaudit/themes' },
      { icon: CheckSquare, label: 'Review Queue', path: '/historical-reaudit/review' },
      { icon: ReceiptText, label: 'Runs, Errors & Spend', path: '/historical-reaudit/runs' },
    ],
  };

  const ringbaMenuItem: MenuItem = {
    icon: GitBranch,
    label: 'Ringba',
    path: '/ringba',
    children: [
      { icon: PhoneCall, label: 'Campaigns', path: '/ringba/campaigns' },
      { icon: GitBranch, label: 'Call flows', path: '/ringba/topology' },
      { icon: BarChart, label: 'Performance & Alerts', path: '/ringba/performance' },
      { icon: ShieldCheck, label: 'ATBS coverage', path: '/ringba/atbs' },
      { icon: ClipboardCopy, label: 'Templates', path: '/ringba/templates' },
    ],
  };

  const walkerMenuItem: MenuItem = {
    icon: FileText,
    label: 'Walker Transcripts',
    path: '/walker-transcripts',
    children: [
      { icon: FileText, label: 'Fetch by ID', path: '/walker-transcripts' },
      { icon: Search, label: 'Search', path: '/walker-transcripts/search' },
      { icon: Bot, label: 'Insights', path: '/walker-transcripts/insights' },
      { icon: History, label: 'Runs', path: '/walker-transcripts/runs' },
      { icon: WalletCards, label: 'Usage & cost', path: '/walker-transcripts/usage' },
      { icon: FlaskConical, label: 'Gold set', path: '/walker-transcripts/gold-set' },
      { icon: Settings, label: 'Walker settings', path: '/walker-transcripts/settings' },
    ],
  };

  const ipqsMenuItem: MenuItem = {
    icon: Shield,
    label: 'IPQS',
    path: '/ipqs',
    children: [
      { icon: LineChart, label: 'Daily Overview', path: '/ipqs' },
      { icon: TableProperties, label: 'Insights Table', path: '/ipqs/insights' },
      { icon: BarChart, label: 'Submission IP Report', path: '/ipqs/submissions' },
      { icon: SearchCheck, label: 'Single Check', path: '/ipqs/check' },
    ],
  };

  // ---------------------------------------------------------------- groups

  const navGroups: NavGroup[] = [
    {
      id: 'domains',
      label: 'Domains',
      icon: Globe,
      items: [
        domainWhitelistMenuItem,
        domainDirectoryMenuItem,
        domainProcessingMenuItem,
        { icon: FileText, label: 'TCPA Languages', path: '/languages' },
      ],
    },
    {
      id: 'calls',
      label: 'Calls & QA',
      icon: Headphones,
      items: [
        atbsMenuItem,
        { icon: MessageSquare, label: 'Customer signals', path: '/customer-signals', children: [
          { icon: ClipboardCheck, label: 'Signal reviews', path: '/customer-signals' },
          { icon: LineChart, label: 'Customer insights', path: '/customer-signals/insights' },
        ] },
        historicalMenuItem,
        { icon: PhoneCall, label: 'Call Lookup', path: '/call-lookup' },
        ringbaMenuItem,
        walkerMenuItem,
      ],
    },
    {
      id: 'leads',
      label: 'Leads & Data',
      icon: Inbox,
      items: [
        { icon: Webhook, label: 'Webhook Data', path: '/webhook-data' },
        ipqsMenuItem,
        { icon: Fingerprint, label: 'TrustedForm Deep Insights', path: '/trustedform' },
        { icon: Database, label: 'Dedupe API', path: '/dedupe-api' },
        { icon: MessageSquare, label: 'Meera SMS Leads', path: '/masstort-coreg' },
        { icon: Rocket, label: 'Meera Lead Manager', path: '/meera-lead-manager' },
      ],
    },
    {
      id: 'advertising',
      label: 'Advertising',
      icon: Megaphone,
      items: [
        { icon: CheckSquare, label: 'WA_ID coverage', path: '/ad-compliance' },
        { icon: Library, label: 'Monitored libraries', path: '/ad-compliance/libraries' },
        { icon: ScanSearch, label: 'IntegriShield', path: '/integrishield' },
      ],
    },
    {
      id: 'operations',
      label: 'Operations',
      icon: Wrench,
      items: [
        { icon: DollarSign, label: 'Spend Monitor', path: '/spend-monitor' },
        { icon: Library, label: 'Resource Hub', path: '/hub' },
        { icon: MessageSquarePlus, label: 'Bugs & Requests', path: '/feedback' },
      ],
    },
    {
      id: 'wip',
      label: 'Work in progress',
      icon: Construction,
      muted: true,
      items: [
        { icon: BarChart, label: 'Ping/Post Analysis', path: '/pingpost' },
        { icon: Bot, label: 'AI Call Insights', path: '/call-insights' },
        { icon: Files, label: 'Lead Documents', path: '/leads' },
        { icon: FilterX, label: 'Filter Management', path: '/filter-management' },
        { icon: Search, label: 'Keyword Watch', path: '/keyword-watch' },
        { icon: Monitor, label: 'Language Monitor', path: '/tcpa-language-monitor' },
        { icon: CheckSquare, label: 'TCPA Reviews', path: '/tcpa-reviews' },
        { icon: Zap, label: 'Legacy Dashboard', path: '/legacy-dashboard' },
      ],
    },
    {
      id: 'drafts',
      label: 'Idea drafts',
      icon: Lightbulb,
      muted: true,
      items: [
        ...(vendorCrmEnabled && profile?.role === 'admin_level_3' ? [{ icon: Handshake, label: 'Vendors', path: '/vendors' }] : []),
        { icon: ClipboardCopy, label: 'Copy Paste', path: '/copy-paste' },
      ],
    },
  ];

  const settingsMenuItem: MenuItem = { icon: Settings, label: 'Settings', path: '/settings' };

  // The standard workspace is short enough to stay flat; grouping five items
  // would add a click without removing a single row.
  const standardMenuItems: MenuItem[] = [
    dashboardMenuItem,
    atbsMenuItem,
    domainProcessingMenuItem,
    domainWhitelistMenuItem,
    domainDirectoryMenuItem,
  ];

  const allItems: MenuItem[] = [
    dashboardMenuItem,
    settingsMenuItem,
    ...navGroups.flatMap((group) => group.items.flatMap((item) => [item, ...(item.children ?? [])])),
  ];

  const pinnedItems = pinnedPaths
    .map((path) => allItems.find((item) => item.path === path))
    .filter((item): item is MenuItem => item !== undefined && canAccessPath(item.path));

  // ---------------------------------------------------------------- routing

  const isWithin = (base: string) =>
    location.pathname === base || location.pathname.startsWith(`${base}/`);

  const isItemActive = (item: MenuItem) => {
    if (item.path === '/') return location.pathname === '/';
    // A parent that owns a submenu highlights only on its own route, so the
    // child rows carry the active state instead of both lighting up.
    if (item.children?.length) return location.pathname === item.path;
    return isWithin(item.path);
  };

  const groupOwningRoute = () =>
    navGroups.find((group) =>
      group.items.some((item) => isWithin(item.path) || (item.children ?? []).some((child) => isWithin(child.path))),
    )?.id ?? null;

  const [openGroupId, setOpenGroupId] = useState<string | null>(groupOwningRoute);

  // Follow the route: landing anywhere opens the group that owns it, which is
  // what keeps a single group open and the menu inside one screen.
  useEffect(() => {
    const owner = groupOwningRoute();
    if (owner) setOpenGroupId(owner);
  }, [location.pathname]);

  useEffect(() => {
    setPinnedPaths(Array.isArray(profile?.pinned_menu_paths) ? profile.pinned_menu_paths : []);
  }, [profile?.pinned_menu_paths]);

  const togglePinnedPath = async (event: MouseEvent<HTMLButtonElement>, path: string) => {
    event.preventDefault();
    event.stopPropagation();

    if (!user) return;

    const previousPaths = pinnedPaths;
    const isPinned = previousPaths.includes(path);
    const nextPaths = isPinned ? previousPaths.filter((p) => p !== path) : [...previousPaths, path];

    setPinnedPaths(nextPaths);

    const { error } = await supabase
      .from('profiles')
      .update({ pinned_menu_paths: nextPaths })
      .eq('id', user.id);

    if (error) {
      setPinnedPaths(previousPaths);
      toast.error('Unable to update pinned tabs');
      return;
    }

    toast.success(isPinned ? 'Tab unpinned' : 'Tab pinned');
  };

  // ---------------------------------------------------------------- render

  const renderMenuItem = (item: MenuItem, options: { depth?: 0 | 1 | 2; showPin?: boolean } = {}) => {
    const Icon = item.icon;
    const depth = options.depth ?? 0;
    const isActive = isItemActive(item);
    const pendingReviewCount = pendingCount ?? 0;
    const showBadge = Boolean(item.showTransferAlert && pendingReviewCount > 0);
    const isPinned = pinnedPaths.includes(item.path);
    const showPin = (options.showPin ?? true) && !isCollapsed;

    // A tool's own pages appear only while you are inside that tool, so depth
    // never costs height you aren't using.
    const children = (item.children ?? []).filter((child) => canAccessPath(child.path));
    const showChildren = children.length > 0 && !isCollapsed && isWithin(item.path);

    const padding = depth === 0 ? 'px-6 py-3' : depth === 1 ? 'px-8 py-2' : 'px-11 py-2';
    const activeBg = depth === 0 ? 'bg-gray-700' : 'bg-gray-600';

    return (
      <div key={`${depth}-${item.path}-${item.label}`}>
        <div className="relative group">
          <Link
            to={item.to || item.path}
            onClick={closeMobileSidebar}
            className={`flex items-center ${padding} text-sm ${
              isActive ? activeBg : 'hover:bg-gray-700'
            } ${showPin ? 'pr-12' : ''}`}
            title={isCollapsed ? item.label : undefined}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon className={depth === 0 ? 'h-5 w-5 min-w-5' : 'h-4 w-4 min-w-4'} />
            {!isCollapsed && (
              <>
                <span className="ml-3">{item.label}</span>
                {showBadge && (
                  <span
                    className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-xs font-bold text-gray-950"
                    aria-label={`${pendingReviewCount} transfer calls pending review`}
                    title="Transferred calls awaiting review in the rolling 7-day window"
                  >
                    <Bell className="h-3 w-3" aria-hidden="true" />
                    {pendingReviewCount}
                  </span>
                )}
              </>
            )}
            {isCollapsed && showBadge && (
              <span
                className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-gray-950 bg-amber-400 rounded-full"
                aria-label={`${pendingReviewCount} transfer calls pending review`}
              >
                {pendingReviewCount > 9 ? '9+' : pendingReviewCount}
              </span>
            )}
          </Link>
          {showPin && (
            <button
              type="button"
              onClick={(event) => togglePinnedPath(event, item.path)}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-600 transition-opacity ${
                isPinned ? 'opacity-100 text-yellow-300' : 'opacity-0 group-hover:opacity-100 text-gray-300'
              }`}
              title={isPinned ? 'Unpin tab' : 'Pin tab'}
              aria-label={isPinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
            >
              {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
            </button>
          )}
        </div>
        {showChildren && (
          <div className="bg-gray-900/60">
            {children.map((child) => renderMenuItem(child, { depth: 2 }))}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (group: NavGroup) => {
    const Icon = group.icon;
    const isOpen = openGroupId === group.id && !isCollapsed;
    const holdsRoute = group.items.some(
      (item) => isWithin(item.path) || (item.children ?? []).some((child) => isWithin(child.path)),
    );

    return (
      <div key={group.id} className={group.muted ? 'border-t border-gray-700/70' : ''}>
        <button
          type="button"
          onClick={() => {
            // Collapsed rail has nowhere to put the list; open the rail first.
            if (isCollapsed) {
              toggleSidebar();
              setOpenGroupId(group.id);
              return;
            }
            setOpenGroupId(isOpen ? null : group.id);
          }}
          aria-expanded={isCollapsed ? undefined : isOpen}
          aria-controls={isCollapsed ? undefined : `nav-group-${group.id}`}
          aria-label={`${group.label}, ${group.items.length} ${group.items.length === 1 ? 'page' : 'pages'}`}
          className={`w-full flex items-center px-6 py-3 text-sm hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ${
            isCollapsed ? 'justify-center' : 'justify-between'
          } ${holdsRoute && !isOpen ? 'bg-gray-700/50' : ''}`}
          title={isCollapsed ? group.label : undefined}
        >
          <span className="flex min-w-0 items-center">
            <Icon className={`h-5 w-5 min-w-5 ${group.muted ? 'text-gray-400' : ''}`} />
            {!isCollapsed && (
              <span className={`ml-3 truncate ${group.muted ? 'text-gray-400' : ''}`}>{group.label}</span>
            )}
          </span>
          {!isCollapsed && (
            <span className="flex items-center gap-2">
              <span className="text-xs tabular-nums text-gray-400" aria-hidden="true">{group.items.length}</span>
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </span>
          )}
        </button>
        {isOpen && (
          <div id={`nav-group-${group.id}`} className="bg-gray-900">
            {group.items.map((item) => renderMenuItem(item, { depth: 1 }))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`fixed left-0 top-0 z-40 flex h-screen flex-col bg-gray-800 text-white transition-all duration-300 will-change-[width] ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div className="p-4 flex items-center justify-between">
        {!isCollapsed && <h2 className="text-xl font-bold">Menu</h2>}
        <button
          onClick={toggleSidebar}
          aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${
            isCollapsed ? 'mx-auto' : ''
          }`}
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      <nav className="mt-4 flex-1 overflow-y-auto overflow-x-hidden min-h-0 sidebar-scrollbar">
        {pinnedItems.length > 0 && (
          <div className="mb-2 border-b border-gray-700 pb-2">
            {!isCollapsed && (
              <p className="px-6 py-2 text-xs uppercase tracking-wide text-gray-400">Pinned</p>
            )}
            {pinnedItems.map((item) => renderMenuItem(item))}
          </div>
        )}

        {viewMode === 'elevated' ? (
          <>
            {renderMenuItem(dashboardMenuItem)}
            {navGroups.map(renderGroup)}
          </>
        ) : (
          <>
            {standardMenuItems.filter((item) => canAccessPath(item.path)).map((item) => renderMenuItem(item))}
            {allItems.filter((item, index, items) =>
              canAccessPath(item.path) &&
              !standardMenuItems.some((standard) => standard.path === item.path || standard.children?.some((child) => child.path === item.path)) &&
              items.findIndex((candidate) => candidate.path === item.path) === index &&
              !items.some((parent) => parent.children?.includes(item) && canAccessPath(parent.path))
            ).map((item) => renderMenuItem(item))}
            {standardMenuItems.filter((item) => !canAccessPath(item.path)).flatMap((item) => item.children ?? [])
              .filter((item) => canAccessPath(item.path)).map((item) => renderMenuItem(item))}
          </>
        )}
      </nav>

      {viewMode === 'elevated' && (
        <div className="border-t border-gray-700 flex-shrink-0">
          {renderMenuItem(settingsMenuItem, { showPin: false })}
        </div>
      )}
    </div>
  );
}
